import { Component, Input, OnInit } from '@angular/core';
import { MenuService } from '../app.menu.service';
import {
    ColorScheme,
    LayoutService,
    MenuMode,
} from '../service/app.layout.service';

@Component({
    selector: 'app-config',
    templateUrl: './app.config.component.html',
})
/**
 * Configuration sidebar component that exposes all layout config options as getter/setter pairs.
 *
 * Every setter calls `layoutService.config.update()` to produce a new config signal value, so
 * consumers are automatically notified.  `menuMode` and `menuProfilePosition` setters also call
 * `menuService.reset()` when switching to slim or horizontal modes.  `colorScheme` setter forces
 * `menuTheme` to `'dark'` when the dark scheme is selected.
 *
 * `ngOnInit` populates the `componentThemes`, `menuThemes`, and `topbarThemes` colour arrays.
 * `decrementScale` / `incrementScale` step through the fixed `scales` array.
 */
export class AppConfigComponent implements OnInit {
    @Input() minimal: boolean = false;

    componentThemes: any[] = [];

    menuThemes: any[] = [];

    topbarThemes: any[] = [];

    scales: number[] = [9, 10, 11, 12, 14, 15];

    constructor(
        public layoutService: LayoutService,
        public menuService: MenuService
    ) {}

    /** `true` when the config sidebar panel is visible. */
    get visible(): boolean {
        return this.layoutService.state.configSidebarVisible;
    }
    /** Shows or hides the config sidebar panel. */
    set visible(_val: boolean) {
        this.layoutService.state.configSidebarVisible = _val;
    }

    /** The current font-scale index value. */
    get scale(): number {
        return this.layoutService.config().scale;
    }
    /** Sets the font-scale value and updates the layout config signal. */
    set scale(_val: number) {
        this.layoutService.config.update((config) => ({
            ...config,
            scale: _val,
        }));
    }

    /** The active menu layout mode (e.g., `'static'`, `'slim'`, `'horizontal'`). */
    get menuMode(): MenuMode {
        return this.layoutService.config().menuMode;
    }
    /** Sets the menu layout mode and resets the menu service when switching to slim/horizontal. */
    set menuMode(_val: MenuMode) {
        this.layoutService.config.update((config) => ({
            ...config,
            menuMode: _val,
        }));
        if (this.layoutService.isSlim() || this.layoutService.isHorizontal()) {
            this.menuService.reset();
        }
    }

    /** The position of the menu-profile panel (`'start'` or `'end'`). */
    get menuProfilePosition(): string {
        return this.layoutService.config().menuProfilePosition;
    }
    /** Sets the menu-profile position and resets the menu service in slim/horizontal modes. */
    set menuProfilePosition(_val: string) {
        this.layoutService.config.update((config) => ({
            ...config,
            menuProfilePosition: _val,
        }));
        if (
            this.layoutService.isSlimPlus() ||
            this.layoutService.isSlim() ||
            this.layoutService.isHorizontal()
        ) {
            this.menuService.reset();
        }
    }

    /** The active colour scheme (`'light'` or `'dark'`). */
    get colorScheme(): ColorScheme {
        return this.layoutService.config().colorScheme;
    }
    /** Sets the colour scheme; forces `menuTheme` to `'dark'` when switching to dark mode. */
    set colorScheme(_val: ColorScheme) {
        this.layoutService.config.update((config) => ({
            ...config,
            menuTheme: _val === 'dark' ? 'dark' : config.menuTheme,
            colorScheme: _val,
        }));
    }

    /** The PrimeNG input style (`'outlined'` or `'filled'`). */
    get inputStyle(): string {
        return this.layoutService.config().inputStyle;
    }
    /** Sets the PrimeNG input style and updates the config signal. */
    set inputStyle(_val: string) {
        this.layoutService.config.update((config) => ({
            ...config,
            inputStyle: _val,
        }));
    }

    /** `true` when PrimeNG ripple effects are enabled. */
    get ripple(): boolean {
        return this.layoutService.config().ripple;
    }
    /** Enables or disables PrimeNG ripple effects. */
    set ripple(_val: boolean) {
        this.layoutService.config.update((config) => ({
            ...config,
            ripple: _val,
        }));
    }

    /** The active sidebar menu colour theme name. */
    get menuTheme(): string {
        return this.layoutService.config().menuTheme;
    }
    /** Sets the sidebar menu colour theme. */
    set menuTheme(val: string) {
        this.layoutService.config.update((config) => ({
            ...config,
            menuTheme: val,
        }));
    }

    /** The active topbar colour theme name. */
    get topbarTheme(): string {
        return this.layoutService.config().topbarTheme;
    }
    /** Sets the topbar colour theme. */
    set topbarTheme(val: string) {
        this.layoutService.config.update((config) => ({
            ...config,
            topbarTheme: val,
        }));
    }

    /** The active PrimeNG component colour theme name. */
    get componentTheme(): string {
        return this.layoutService.config().componentTheme;
    }
    /** Sets the PrimeNG component colour theme. */
    set componentTheme(val: string) {
        this.layoutService.config.update((config) => ({
            ...config,
            componentTheme: val,
        }));
    }

    /** Populates the `componentThemes`, `menuThemes`, and `topbarThemes` colour arrays. */
    ngOnInit() {
        this.componentThemes = [
            { name: 'indigo', color: '#3F51B5' },
            { name: 'pink', color: '#E91E63' },
            { name: 'purple', color: '#9C27B0' },
            { name: 'deeppurple', color: '#673AB7' },
            { name: 'blue', color: '#2196F3' },
            { name: 'lightblue', color: '#03A9F4' },
            { name: 'cyan', color: '#00BCD4' },
            { name: 'teal', color: '#009688' },
            { name: 'green', color: '#4CAF50' },
            { name: 'lightgreen', color: '#8BC34A' },
            { name: 'lime', color: '#CDDC39' },
            { name: 'yellow', color: '#FFEB3B' },
            { name: 'amber', color: '#FFC107' },
            { name: 'orange', color: '#FF9800' },
            { name: 'deeporange', color: '#FF5722' },
            { name: 'brown', color: '#795548' },
            { name: 'bluegrey', color: '#607D8B' },
        ];

        this.menuThemes = [
            { name: 'light', color: '#FDFEFF' },
            { name: 'dark', color: '#434B54' },
            { name: 'indigo', color: '#1A237E' },
            { name: 'bluegrey', color: '#37474F' },
            { name: 'brown', color: '#4E342E' },
            { name: 'cyan', color: '#006064' },
            { name: 'green', color: '#2E7D32' },
            { name: 'deeppurple', color: '#4527A0' },
            { name: 'deeporange', color: '#BF360C' },
            { name: 'pink', color: '#880E4F' },
            { name: 'purple', color: '#6A1B9A' },
            { name: 'teal', color: '#00695C' },
        ];

        this.topbarThemes = [
            { name: 'lightblue', color: '#2E88FF' },
            { name: 'dark', color: '#363636' },
            { name: 'white', color: '#FDFEFF' },
            { name: 'blue', color: '#1565C0' },
            { name: 'deeppurple', color: '#4527A0' },
            { name: 'purple', color: '#6A1B9A' },
            { name: 'pink', color: '#AD1457' },
            { name: 'cyan', color: '#0097A7' },
            { name: 'teal', color: '#00796B' },
            { name: 'green', color: '#43A047' },
            { name: 'lightgreen', color: '#689F38' },
            { name: 'lime', color: '#AFB42B' },
            { name: 'yellow', color: '#FBC02D' },
            { name: 'amber', color: '#FFA000' },
            { name: 'orange', color: '#FB8C00' },
            { name: 'deeporange', color: '#D84315' },
            { name: 'brown', color: '#5D4037' },
            { name: 'grey', color: '#616161' },
            { name: 'bluegrey', color: '#546E7A' },
            { name: 'indigo', color: '#3F51B5' },
        ];
    }

    /** Opens the configuration sidebar panel. */
    onConfigButtonClick() {
        this.layoutService.showConfigSidebar();
    }

    /** Convenience wrapper — sets the component theme via the `componentTheme` setter. */
    changeTheme(theme: string) {
        this.componentTheme = theme;
    }

    /** Convenience wrapper — sets the topbar theme via the `topbarTheme` setter. */
    changeTopbarTheme(theme: string) {
        this.topbarTheme = theme;
    }

    /** Convenience wrapper — sets the menu theme via the `menuTheme` setter. */
    changeMenuTheme(theme: string) {
        this.menuTheme = theme;
    }

  /** Steps the scale down one position in the `scales` array (no-op at the minimum). */
  decrementScale() {
    let i = this.scales.indexOf(this.scale);
    if (i > 0) {
      this.scale = this.scales[i - 1];
    }
  }

  /** Steps the scale up one position in the `scales` array (no-op at the maximum). */
  incrementScale() {
    let i = this.scales.indexOf(this.scale);
    if (i < this.scales.length - 1) {
      this.scale = this.scales[i + 1];
    }
  }
}
